import Button from '@rio-cloud/rio-uikit/Button';
import DayPicker, { type DateRange } from '@rio-cloud/rio-uikit/DayPicker';
import { isMobileScreen } from '@rio-cloud/rio-uikit/DeviceUtils';
import { useIntl } from 'react-intl';

import { DATE_PERIODS, type DatePeriod } from './datePeriod.ts';

export type CustomDateRangePickerProps = {
    id: string;
    period: DatePeriod;
    setPeriod: (period: Exclude<DatePeriod, 'custom'>) => void;
    value?: DateRange;
    onChange: (value: DateRange | undefined) => void;
    usePortal?: boolean;
};

export const CustomDateRangePicker = ({
    id,
    period,
    setPeriod,
    value,
    onChange,
    usePortal,
}: CustomDateRangePickerProps) => {
    const intl = useIntl();
    const message = (key: string) =>
        intl.formatMessage({ id: `intl-msg:ordercom-cloud-frontend.tours.order-filters.${key}` });

    return (
        <DayPicker
            className='margin-bottom-0'
            id={id}
            mode='range'
            locale={intl.locale}
            defaultMonth={value?.from}
            value={value}
            onChange={onChange}
            selectionBehavior='deferred'
            closeOnSelect={false}
            usePortal={usePortal}
            placeholder={message(period)}
            inputProps={{ 'aria-label': message('period'), autoComplete: 'off' }}
            leftSidebarClassName={isMobileScreen() ? undefined : 'border border-color-lighter border-right-only'}
            leftSidebar={({ close }) => (
                <div className='display-flex flex-column gap-5 padding-10'>
                    {DATE_PERIODS.filter(option => option !== 'custom').map(option => (
                        <Button
                            key={option}
                            type='button'
                            bsStyle={period === option ? Button.PRIMARY : Button.MUTED}
                            onClick={() => {
                                setPeriod(option);
                                close();
                            }}
                        >
                            {message(option)}
                        </Button>
                    ))}
                </div>
            )}
            footerClassName='padding-15 border border-top-only border-color-lighter'
            footer={({ apply, clear, close, draftValue }) => (
                <div className='display-flex justify-content-between gap-10'>
                    <Button
                        type='button'
                        bsStyle={Button.MUTED}
                        onClick={() => {
                            clear();
                            setPeriod('all');
                            close();
                        }}
                    >
                        {intl.formatMessage({ id: 'intl-msg:ordercom-cloud-frontend.common.clear' })}
                    </Button>
                    <Button
                        type='button'
                        bsStyle={Button.PRIMARY}
                        onClick={apply}
                        disabled={
                            !draftValue?.from ||
                            !draftValue.to ||
                            (draftValue.from.getTime() === value?.from?.getTime() &&
                                draftValue.to.getTime() === value?.to?.getTime())
                        }
                    >
                        {intl.formatMessage({ id: 'intl-msg:ordercom-cloud-frontend.common.apply' })}
                    </Button>
                </div>
            )}
        />
    );
};
